﻿import { Country } from "../Country";
import { CustomerAddress } from "./CustomerAddress";

export class PostalAddress implements IPostalAddress {
    constructor(postal?: IPostalAddress, address?: CustomerAddress) {
        // initialize typed object from generic object
        this._address = address;
        this.line1 = postal?.line1;
        this.line2 = postal?.line2;
        this.line3 = postal?.line3;
        this.city = postal?.city;
        this.stateOrProvince = postal?.stateOrProvince;
        this.postalCode = postal?.postalCode;
        this.countryCode = postal?.countryCode ?? Country.defaultCode;
    }

    toJSON(): object {
        // return generic object with properties to stringify
        return {
            line1: this.line1 || undefined,
            line2: this.line2 || undefined,
            line3: this.line3 || undefined,
            city: this.city || undefined,
            stateOrProvince: this.stateOrProvince || undefined,
            postalCode: this.postalCode || undefined,
            countryCode: this.countryCode || undefined
        };
    }

    line1?: string;
    line2?: string;
    line3?: string;
    city?: string;
    stateOrProvince?: string;
    postalCode?: string;
    countryCode: string;

    private _address?: CustomerAddress;

    // Calculated properties

    get country(): Country {
        return new Country(this.countryCode);
    }

    get postalFormat(): string {
        return this.country.postalFormat;
    }

    toCityString(): string {
        let s = "";

        if (this.city)
            s += this.city;
        if (this.stateOrProvince)
            s += (s ? ", " : "") + this.stateOrProvince;
        if (this.countryCode && this.countryCode !== 'USA')
            s += (s ? "  " : "") + this.country?.name ?? this.countryCode;
        return s;
    }

    toString(singleLine: boolean = false): string {
        let lines = "";
        if (this.line1)
            lines += this.line1 + (singleLine ? " " : "\r\n");
        if (this.line2)
            lines += this.line2 + (singleLine ? " " : "\r\n");
        if (this.line3)
            lines += this.line3 + (singleLine ? " " : "\r\n");

        let s = "";
        const rx = /\{(\d+)(?::(.+?))?\}/g;
        const postalFormat = this.country.postalFormat;
        let i = 0;
        let match = rx.exec(postalFormat);
        while (match) {
            s += postalFormat.substring(i, rx.lastIndex - match[0].length);

            if (match[1] === "0") s += this.format(this.city, match[2]);
            else if (match[1] === "1") s += this.format(this.stateOrProvince, match[2]);
            else if (match[1] === "2") s += this.format(this.postalCode, match[2]);

            i = rx.lastIndex;
            match = rx.exec(postalFormat);
        }
        s += postalFormat.substring(i, postalFormat.length);
        lines += s;

        if (singleLine)
            lines = lines.replace("\r\n", " ");
        
        return lines.trim();
    }

    private format(value?: string, format?: string): string {
        if (!format) return value ?? "";
        switch (format) {
        case "town":
        case "city":
        case "emirate":
        case "locality":
        case "island":
        case "state":
        case "zone":
        case "province":
        case "county":
        case "district":
        case "postcode":
        case "zipcode":
            return value?.toUpperCase() ?? "";
        default:
            // NOTE: For now we just output the value, but in future we should use 'a', 'n', 'x' in format to format the value
            return value?.toUpperCase() ?? "";
        }
    }
}

export interface IPostalAddress {
    line1?: string;
    line2?: string;
    line3?: string;
    city?: string;
    stateOrProvince?: string;
    postalCode?: string;
    countryCode: string;
}
